import {
  AutocompleteInput,
  Create,
  Datagrid,
  DateField,
  DateInput,
  DeleteButton,
  Edit,
  EditButton,
  Filter,
  Labeled,
  List,
  NumberField,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  required,
  SaveButton,
  SearchInput,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  usePermissions,
  downloadCSV,
  useNotify,
  useDataProvider,
  useInput
} from "react-admin";
import UploadImage from "../shared/uploadImage";
import jsonExport from 'jsonexport/dist';
import { useEffect, useRef, useState } from "react";
import { Card, CardContent, Divider, Grid, Typography } from "@mui/material";
import UserDialog from "./components/user-details";
import LinkedContributions from "./components/inkind/LinkedContributions";
import { useIsManager, ReadOnlyListActions } from "./components/manager-read-only";

const token = JSON.parse(localStorage.getItem('token'));

// 👇 Filter component for the Offer list
const OfferFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
    <SelectInput
      source="offer_type"
      label="Type"
      choices={[
        { id: "sell", name: "Sell" },
        { id: "buy", name: "Buy" },
      ]}
      allowEmpty
    />
    <ReferenceInput source="subCategory_id" reference="sub_category" label="Sub Category" allowEmpty>
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <TextInput source="owner" label="Owner ID" />
    <DateInput source="created_at_gte" label="Created After" />
    <DateInput source="created_at_lte" label="Created Before" />
  </Filter>
);
const customExporter = (records) => {
  const csvData = records.map(record => ({
    Owner_Name: record.user?.name || "",
    Owner_PhoneNumber: record.user?.phonenumber ? `'${record.user.phonenumber}` : "",
    SubCategoryName: record.subCategory?.name || "",
    Price: record.price || "",
    Offer: record.offer || "",
    Quantity: record.quantity || "",
    OfferType: record.offer_type || "",
    PricePerUnit: record.price_per_unit || "",
    CreatedAt: record.created_at ? new Date(record.created_at).toLocaleDateString() : "",
    Status: record.status || "",
  }));

  jsonExport(csvData, { rowDelimiter: ',' }, (err, csv) => {
    if (err) {
      console.error(err);
      return;
    }
    // Add UTF-8 BOM to fix Arabic characters in Excel
    const bom = '\uFEFF';
    downloadCSV(bom + csv, 'offers_export');
  });
};

export const OfferList = (props) => {
  const isManager = useIsManager();
  return (
    <>
      <List
        {...props}
        filters={<OfferFilter />}
        exporter={customExporter}
        actions={isManager ? <ReadOnlyListActions /> : undefined}
      >
        <Datagrid rowClick="show">
          <TextField source="id" />
          <TextField source="offer" />
          <TextField source="user.phonenumber" />
          <TextField source="quantity" />
          <TextField source="offer_type" label="Type" />
          <DateField source="created_at" label="Date" />
          <ShowButton></ShowButton>
          {!isManager && <EditButton></EditButton>}
          {!isManager && <DeleteButton mutationMode="pessimistic" />}
        </Datagrid>
      </List>
    </>
  );
};

// Custom User Input Component with direct fetch
const UserPhoneInput = (props) => {
  const { source, validate } = props;
  const { field, fieldState } = useInput({ source, validate });
  const notify = useNotify();

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const timerRef = useRef();

  const searchUsers = async (searchText) => {
    try {
      // Direct fetch call to match your backend API format
      const response = await fetch(`${process.env.REACT_APP_URL}/admin/users?search=${encodeURIComponent(searchText)}&page=1&limit=25`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (inputValue.length < 3) {
      setOptions([]);
      setShowDropdown(false);
      return;
    }

    // Clear previous timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set new timer for debounced search
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        console.log('Searching for users with:', inputValue);
        const users = await searchUsers(inputValue);
        console.log('Search results:', users);
        setOptions(users);
        setShowDropdown(users.length > 0);
      } catch (error) {
        console.error('Error searching users:', error);
        notify('Error searching users', { type: 'error' });
        setOptions([]);
        setShowDropdown(false);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [inputValue, notify]);

  // Load initial value if editing
  useEffect(() => {
    if (field.value && typeof field.value === 'string') {
      // If we have a user ID but no options loaded, try to fetch the user
      fetch(`${process.env.REACT_APP_URL}/admin/users/${field.value}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }
      )
        .then(response => response.json())
        .then(user => {
          setOptions([user]);
          setSelectedUser(user);
          setInputValue(`${user.phonenumber}${user.name ? ` - ${user.name}` : ''}`);
        })
        .catch(error => {
          console.error('Error fetching user:', error);
        });
    }
  }, [field.value]);

  const handleInputChange = (event) => {
    const newInputValue = event.target.value;
    setInputValue(newInputValue);

    // Only clear selection if user is typing new search (not just focusing)
    if (newInputValue !== selectedUser?.phonenumber) {
      setSelectedUser(null);
      field.onChange('');
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setInputValue(`${user.phonenumber}${user.name ? ` - ${user.name}` : ''}`);
    field.onChange(user.id);
    setShowDropdown(false);
  };

  const handleInputFocus = () => {
    if (options.length > 0 && inputValue.length >= 3) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for click selection
    setTimeout(() => setShowDropdown(false), 150);
  };

  const handleDropdownMouseDown = (event) => {
    // Prevent the input from losing focus when clicking dropdown items
    event.preventDefault();
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
    setInputValue('');
    field.onChange('');
    setOptions([]);
  };

  return (
    <div className="space-y-2 relative">
      <label className="block text-sm font-medium text-gray-700">User Phone *</label>

      {/* Custom search input */}
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder="Type at least 3 characters to search users by phone number..."
          className={`bg-white w-full px-3 py-2 border rounded-md shadow-sm ${fieldState.error ? 'border-red-500' : 'border-gray-300'
            } ${selectedUser ? 'bg-green-50' : ''}`}
          disabled={loading}
        />

        {/* Loading indicator */}
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Clear button when user is selected */}
        {selectedUser && !loading && (
          <button
            type="button"
            onClick={handleClearSelection}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        )}
      </div>

      {/* Dropdown options */}
      {showDropdown && options.length > 0 && (
        <div 
          className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          onMouseDown={handleDropdownMouseDown} // Add this to prevent blur
        >
          {options.map((user) => (
            <div
              key={user.id}
              onClick={() => handleUserSelect(user)}
              className="px-4 py-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors duration-150"
            >
              <div className="font-medium text-gray-900">{user.phonenumber}</div>
              {user.name && (
                <div className="text-sm text-gray-600">{user.name}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {showDropdown && inputValue.length >= 3 && options.length === 0 && !loading && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="px-4 py-2 text-gray-500 text-sm">
            No users found for "{inputValue}"
          </div>
        </div>
      )}

      {/* Selected user confirmation */}
      {selectedUser && (
        <div className="p-2 bg-green-50 border border-green-200 rounded-md">
          <p className="text-xs text-green-700">
            ✓ Selected: <span className="font-semibold">{selectedUser.phonenumber}</span>
            {selectedUser.name && ` - ${selectedUser.name}`}
          </p>
        </div>
      )}

      {/* Error message */}
      {fieldState.error && (
        <p className="text-sm text-red-600">{fieldState.error.message}</p>
      )}

      <p className="text-xs text-gray-500">
        Start typing a phone number (minimum 3 characters) to search users
      </p>
    </div>
  );
};

// Main OfferCreate Component
export const OfferCreate = (props) => {
  const { permissions } = usePermissions();
  const canEdit = permissions === "admin" || permissions === "operater";

  const transform = (data) => {
    console.log('Original form data:', data);

    const transformed = {
      owner: data.owner,
      category_id: Number(data.category_id),
      subCategory_id: Number(data.subCategory_id),
      offer: data.offer,
      quantity: data.quantity,
      price: data.price ? Number(data.price) : undefined,
      unit: String(data.unit),
      price_per_unit: data.price_per_unit ? Number(data.price_per_unit) : undefined,
      location: data.location || undefined,
      offer_type: data.offer_type || 'sell',
      currency: 'SDG',
      state: String(data.state),
      locality: data.locality ? String(data.locality) : null,
      // city: String(data.city),
      market: data.market ? String(data.market) : null
    };

    console.log('Transformed data:', transformed);
    return transformed;
  };

  return (
    canEdit ?
      <Create {...props} transform={transform} redirect="list">
        <SimpleForm
          toolbar={null}
          className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md">
          {/* Row 1: User, Category, Subcategory */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Custom User Phone Input */}
            <UserPhoneInput source="owner" validate={[required()]} />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Category *</label>
              <ReferenceInput source="category_id" reference="category" validate={[required()]}>
                <AutocompleteInput
                  optionText="name"
                  optionValue="id"
                  label={false}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </ReferenceInput>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Subcategory *</label>
              <ReferenceInput source="subCategory_id" reference="sub_category" validate={[required()]}>
                <AutocompleteInput
                  optionText="name"
                  optionValue="id"
                  label={false}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </ReferenceInput>
            </div>
          </div>

          {/* Row 2: Offer Description and Type */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="md:col-span-3 space-y-2">
              <label className="block text-sm font-medium text-gray-700">Offer Description *</label>
              <TextInput
                multiline
                source="offer"
                validate={[required()]}
                label={false}
                fullWidth
                className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Offer Type</label>
              <SelectInput
                source="offer_type"
                choices={[
                  { id: "sell", name: "Sell" },
                  { id: "buy", name: "Buy" },
                ]}
                defaultValue="sell"
                label={false}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Row 3: Pricing Information */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Quantity *</label>
              <TextInput
                source="quantity"
                validate={[required()]}
                label={false}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Price</label>
              <NumberInput
                source="price"
                min={0}
                label={false}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Unit *</label>
              <ReferenceInput source="unit" reference="unit" validate={[required()]}>
                <SelectInput
                  optionText="name"
                  optionValue="id"
                  label={false}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </ReferenceInput>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Price Per Unit</label>
              <NumberInput
                source="price_per_unit"
                min={0}
                label={false}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Row 4: Location Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">State *</label>
              <ReferenceInput source="state" reference="state" validate={[required()]}>
                <SelectInput
                  optionText="name"
                  optionValue="id"
                  label={false}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </ReferenceInput>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Locality (Optional)</label>
              <ReferenceInput source="locality" reference="locality">
                <SelectInput
                  optionText="name"
                  optionValue="id"
                  label={false}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </ReferenceInput>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Market (Optional)</label>
              <ReferenceInput source="market" reference="market">
                <SelectInput
                  optionText="name"
                  optionValue="id"
                  label={false}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </ReferenceInput>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end mt-8 pt-6 border-t border-gray-200">
            <SaveButton
              label="Create Offer"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md shadow-sm transition duration-200"
            />
          </div>
        </SimpleForm>
      </Create>
      : undefined
  );
};

const UserEditToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

export const OfferEdit = (props) => {
  const isManager = useIsManager();
  const transform = (data) => {
    const transformed = {
      category_id: Number(data.category_id),
      subCategory_id: Number(data.subCategory_id),
      offer: data.offer,
      quantity: data.quantity,
      price: data.price ? Number(data.price) : undefined,
      unit: String(data.unit),
      price_per_unit: data.price_per_unit ? Number(data.price_per_unit) : undefined,
      offer_type: data.offer_type,
      state: String(data.state),
      locality: data.locality ? String(data.locality) : null,
      market: data.market ? String(data.market) : null,
    };

    return transformed;
  };

  // Managers have read-only access to offers
  if (isManager) return null;

  return (
    <Edit {...props} transform={transform}>
      <SimpleForm
        toolbar={<UserEditToolbar />}
        className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md">
        {/* Row 1: Category, Subcategory */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Category *</label>
            <ReferenceInput source="category_id" reference="category" validate={[required()]}>
              <AutocompleteInput
                optionText="name"
                optionValue="id"
                label={false}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </ReferenceInput>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Subcategory *</label>
            <ReferenceInput source="subCategory_id" reference="sub_category" validate={[required()]}>
              <AutocompleteInput
                optionText="name"
                optionValue="id"
                label={false}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </ReferenceInput>
          </div>
        </div>

        {/* Row 2: Offer Description and Type */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="md:col-span-3 space-y-2">
            <label className="block text-sm font-medium text-gray-700">Offer Description *</label>
            <TextInput
              multiline
              source="offer"
              validate={[required()]}
              label={false}
              fullWidth
              className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Offer Type</label>
            <SelectInput
              source="offer_type"
              choices={[
                { id: "sell", name: "Sell" },
                { id: "buy", name: "Buy" },
              ]}
              label={false}
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Row 3: Pricing Information */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Quantity *</label>
            <TextInput
              source="quantity"
              validate={[required()]}
              label={false}
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Price</label>
            <NumberInput
              source="price"
              min={0}
              label={false}
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Unit *</label>
            <ReferenceInput source="unit" reference="unit" validate={[required()]}>
              <SelectInput
                optionText="name"
                optionValue="id"
                label={false}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </ReferenceInput>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Price Per Unit</label>
            <NumberInput
              source="price_per_unit"
              min={0}
              label={false}
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Row 4: Location Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">State *</label>
            <ReferenceInput source="state" reference="state" validate={[required()]}>
              <SelectInput
                optionText="name"
                optionValue="id"
                label={false}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </ReferenceInput>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Locality (Optional)</label>
            <ReferenceInput source="locality" reference="locality">
              <SelectInput
                optionText="name"
                optionValue="id"
                label={false}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </ReferenceInput>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Market (Optional)</label>
            <ReferenceInput source="market" reference="market">
              <SelectInput
                optionText="name"
                optionValue="id"
                label={false}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </ReferenceInput>
          </div>
        </div>
      </SimpleForm>
    </Edit>
  );
};

export const OfferShow = (props) => {
  const isManager = useIsManager();
  return (
  <Show
    {...props}
    title="Offer Details"
    actions={isManager ? false : undefined}
  >
    <Card sx={{ mx: "auto", p: 2 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Offer Details
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Labeled label="ID">
              <TextField source="id" />
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Labeled label="Type">
              <TextField source="offer_type" />
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Labeled label="Status">
              <TextField source="status" />
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Labeled label="Created At">
              <DateField source="created_at" showTime />
            </Labeled>
          </Grid>

          <Grid item xs={12}>
            <Labeled label="Offer Description">
              <TextField source="offer" />
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Labeled label="Quantity">
              <TextField source="quantity" />
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Labeled label="Price">
              <NumberField source="price" />
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Labeled label="Price Per Unit">
              <NumberField source="price_per_unit" />
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Labeled label="Unit">
              <ReferenceField source="unit" reference="unit" link={false}>
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Labeled label="Category">
              <ReferenceField source="category_id" reference="category" link={false}>
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Labeled label="Sub Category">
              <ReferenceField source="subCategory_id" reference="sub_category" link={false}>
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Labeled label="Owner">
              <ReferenceField source="owner" reference="users" link={false}>
                <UserDialog source="owner" />
              </ReferenceField>
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Labeled label="State">
              <ReferenceField source="state" reference="state" link={false}>
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Labeled label="Locality">
              <ReferenceField source="locality" reference="locality" link={false} emptyText="-">
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Labeled label="Market">
              <ReferenceField source="market" reference="market" link={false} emptyText="-">
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Grid>
        </Grid>

        <LinkedContributions resourceType="offers" />
      </CardContent>
    </Card>
  </Show>
  );
};
